/**
 * Data-binding connector manifests.
 *
 * Registers every shared file/cloud source as a {@link ConnectorManifest}.
 * Called once at bootstrap; picker UI then emerges from filtering.
 *
 * Adapter modules are loaded lazily so their code only hits the bundle when
 * the user actually picks a connector.
 */
import { registerConnector } from '../../../core/data-profiles/connectorRegistry';

/**
 * Idempotent — safe to call multiple times per session. The underlying
 * registry throws on duplicate IDs, so we catch that narrowly and proceed.
 */
export function registerDataBindingConnectors(): void {
  safeRegister({
    id: 'csv-upload',
    label: 'CSV Upload',
    description: 'Upload a CSV file (one-time import).',
    iconRef: '/assets/app-icons/data-integration/csv-import.svg',
    format: 'file',
    inputHint: 'Comma-separated values with a header row (first column is used as the record key)',
    folderKinds: [
      'orgchart',
      'process',
      'network',
      'grc',
      'strategy',
      'software_design',
      'workspace',
    ],
    diagramTypes: ['org', 'flowchart', 'network'],
    targets: ['diagram', 'folder', 'both'],
    compatibleProfileIds: [
      'orgchart',
      'process',
      'network',
      'grc-control',
      'gantt',
      'component',
    ],
    source: {
      system: 'csv',
      defaultIntent: 'generic_table',
      supportedIntents: ['people_directory', 'person_hierarchy', 'position_hierarchy', 'org_structure', 'team_membership', 'project_allocation', 'capacity_commitment', 'workforce_demand', 'skill_catalog', 'skill_assertion', 'credential_assertion', 'skill_requirement', 'talent_observation', 'succession_planning', 'process_inventory'],
      requiresIntentResolution: true,
      retentionClass: 'current_only',
      volume: 'medium',
      authorityTypes: ['people_identity', 'position_structure', 'team_membership', 'project_allocation', 'capacity_commitment', 'workforce_demand', 'skill_catalog', 'skill_assertion', 'credential_assertion', 'skill_requirement', 'talent_observation', 'succession_plan', 'process_inventory'],
      maxSensitivity: 'hr_restricted',
      yjsSafe: false,
      enablementGates: ['contract', 'identity', 'recovery', 'security', 'scale', 'product'],
    },
    status: 'stable',
    templates: [
      {
        label: 'Org chart hierarchy (sample)',
        href: '/sample-data/enterprise-org-25-hierarchy.csv',
        folderKinds: ['orgchart'],
      },
      {
        label: 'Process inventory (sample)',
        href: '/sample-data/process-inventory-sample.csv',
        folderKinds: ['process'],
      },
    ],
    adapter: () =>
      import('../services/adapters/CsvUploadAdapter').then(
        (m) => new m.CsvUploadAdapter() as never,
      ),
  });

  safeRegister({
    id: 'google-sheets',
    label: 'Google Sheets',
    description: 'Connect spreadsheets in Google Drive.',
    iconRef: '/assets/app-icons/data-integration/sheets-import.svg',
    format: 'oauth',
    folderKinds: ['orgchart', 'process', 'grc', 'strategy', 'software_design', 'workspace'],
    targets: ['diagram', 'folder', 'both'],
    compatibleProfileIds: ['orgchart', 'process', 'grc-control', 'gantt', 'component'],
    source: {
      system: 'google_sheets',
      defaultIntent: 'generic_table',
      supportedIntents: ['people_directory', 'person_hierarchy', 'position_hierarchy', 'org_structure', 'team_membership', 'project_allocation', 'capacity_commitment', 'workforce_demand', 'skill_catalog', 'skill_assertion', 'credential_assertion', 'skill_requirement', 'talent_observation', 'succession_planning'],
      requiresIntentResolution: true,
      emitsDeltas: true,
      retentionClass: 'rolling_window',
      volume: 'medium',
      authorityTypes: ['people_identity', 'position_structure', 'team_membership', 'project_allocation', 'capacity_commitment', 'workforce_demand', 'skill_catalog', 'skill_assertion', 'credential_assertion', 'skill_requirement', 'talent_observation', 'succession_plan'],
      maxSensitivity: 'hr_restricted',
      yjsSafe: false,
      enablementGates: ['contract', 'identity', 'recovery', 'security', 'scale', 'product'],
    },
    status: 'stable',
    adapter: () =>
      import('../services/adapters/GoogleSheetsAdapter').then(
        (m) => new m.GoogleSheetsAdapter() as never,
      ),
  });

  safeRegister({
    id: 'excel-online',
    label: 'Excel Online',
    description: 'Connect to Microsoft Excel Workbooks.',
    iconRef: '/assets/app-icons/data-integration/excel.png',
    format: 'oauth',
    folderKinds: ['orgchart', 'process', 'grc', 'strategy', 'software_design', 'workspace'],
    targets: ['diagram', 'folder', 'both'],
    compatibleProfileIds: ['orgchart', 'process', 'grc-control', 'gantt', 'component'],
    source: {
      system: 'excel',
      defaultIntent: 'generic_table',
      supportedIntents: ['people_directory', 'person_hierarchy', 'position_hierarchy', 'org_structure', 'team_membership', 'project_allocation', 'capacity_commitment', 'workforce_demand', 'skill_catalog', 'skill_assertion', 'credential_assertion', 'skill_requirement', 'talent_observation', 'succession_planning'],
      requiresIntentResolution: true,
      emitsDeltas: true,
      retentionClass: 'rolling_window',
      volume: 'medium',
      authorityTypes: ['people_identity', 'position_structure', 'team_membership', 'project_allocation', 'capacity_commitment', 'workforce_demand', 'skill_catalog', 'skill_assertion', 'credential_assertion', 'skill_requirement', 'talent_observation', 'succession_plan'],
      maxSensitivity: 'hr_restricted',
      yjsSafe: false,
      enablementGates: ['contract', 'identity', 'recovery', 'security', 'scale', 'product'],
    },
    status: 'stable',
    adapter: () =>
      import('../services/adapters/ExcelOnlineAdapter').then(
        (m) => new m.ExcelOnlineAdapter() as never,
      ),
  });

  safeRegister({
    id: 'jira-export',
    label: 'Jira export',
    description: 'Import Jira issues or plans from a CSV export.',
    iconRef: 'assets/connectors/jira.svg',
    format: 'file',
    inputHint: 'CSV export with Issue key, Summary, Status, Assignee, Sprint, or Epic columns',
    folderKinds: ['orgchart', 'process', 'strategy', 'software_design', 'workspace'],
    targets: ['folder', 'both'],
    compatibleProfileIds: ['gantt', 'orgchart', 'process', 'component'],
    source: {
      system: 'jira',
      defaultIntent: 'task_tracker',
      requiresIntentResolution: true,
      retentionClass: 'rolling_window',
      volume: 'high',
    },
    status: 'beta',
    expectedColumns: ['Issue key', 'Summary', 'Status', 'Assignee', 'Sprint', 'Epic Link'],
    adapter: () =>
      import('../services/adapters/CsvUploadAdapter').then(
        (m) => new m.CsvUploadAdapter() as never,
      ),
  });

  safeRegister({
    id: 'github-issues-export',
    label: 'GitHub issues export',
    description: 'Import GitHub issues, pull requests, or planning exports as task facts.',
    iconRef: 'assets/connectors/github.svg',
    format: 'file',
    inputHint: 'CSV with number, title, state/status, assignee, labels, milestone, or project columns',
    folderKinds: ['orgchart', 'process', 'strategy', 'software_design', 'workspace'],
    targets: ['folder', 'both'],
    compatibleProfileIds: ['gantt', 'component', 'orgchart', 'process'],
    source: {
      system: 'github',
      defaultIntent: 'task_tracker',
      requiresIntentResolution: true,
      retentionClass: 'rolling_window',
      volume: 'high',
    },
    status: 'beta',
    expectedColumns: ['number', 'title', 'state', 'assignee', 'labels', 'milestone'],
    adapter: () =>
      import('../services/adapters/CsvUploadAdapter').then(
        (m) => new m.CsvUploadAdapter() as never,
      ),
  });

  safeRegister({
    id: 'asana-export',
    label: 'Asana export',
    description: 'Import Asana tasks, projects, and assignments from a CSV export.',
    iconRef: 'assets/connectors/asana.svg',
    format: 'file',
    inputHint: 'CSV export with Task ID, Name, Assignee, Status/Completed, Project, Due Date, or Section columns',
    folderKinds: ['orgchart', 'process', 'strategy', 'workspace'],
    targets: ['folder', 'both'],
    compatibleProfileIds: ['gantt', 'orgchart', 'process'],
    source: {
      system: 'asana',
      defaultIntent: 'task_tracker',
      requiresIntentResolution: true,
      retentionClass: 'rolling_window',
      volume: 'high',
    },
    status: 'beta',
    expectedColumns: ['Task ID', 'Name', 'Assignee', 'Completed', 'Project', 'Due Date'],
    adapter: () =>
      import('../services/adapters/CsvUploadAdapter').then(
        (m) => new m.CsvUploadAdapter() as never,
      ),
  });

  safeRegister({
    id: 'access-grants-export',
    label: 'Access grants export',
    description: 'Import user, group, application, and entitlement exports from IdP or IAM systems.',
    iconRef: 'assets/connectors/access.svg',
    format: 'file',
    inputHint: 'CSV with User/Email, Application/System, Permission/Entitlement, Group/Role columns',
    folderKinds: ['orgchart', 'process', 'grc', 'workspace'],
    targets: ['folder', 'both'],
    compatibleProfileIds: ['orgchart', 'grc-control', 'process'],
    source: {
      system: 'identity_access',
      defaultIntent: 'access_grants',
      requiresIntentResolution: true,
      retentionClass: 'audit_retained',
      volume: 'high',
    },
    status: 'beta',
    expectedColumns: ['User', 'Email', 'Application', 'Permission', 'Group'],
    adapter: () =>
      import('../services/adapters/CsvUploadAdapter').then(
        (m) => new m.CsvUploadAdapter() as never,
      ),
  });

  safeRegister({
    id: 'psa-capacity-export',
    label: 'PSA capacity export',
    description: 'Import resource plans, assignments, and project staffing exports from PSA systems.',
    iconRef: 'assets/connectors/psa.svg',
    format: 'file',
    inputHint: 'CSV with Person/Email, Project/Engagement, Role, Allocation/FTE, Start, End, or Hours columns',
    folderKinds: ['orgchart', 'strategy', 'workspace'],
    targets: ['folder', 'both'],
    compatibleProfileIds: ['orgchart', 'gantt'],
    source: {
      system: 'psa',
      defaultIntent: 'capacity_commitment',
      requiresIntentResolution: true,
      retentionClass: 'rolling_window',
      volume: 'high',
    },
    status: 'beta',
    expectedColumns: ['Person', 'Email', 'Project', 'Role', 'Allocation', 'Start Date', 'End Date'],
    adapter: () =>
      import('../services/adapters/CsvUploadAdapter').then(
        (m) => new m.CsvUploadAdapter() as never,
      ),
  });

  safeRegister({
    id: 'finance-plan-export',
    label: 'Finance plan export',
    description: 'Import cost-rate, budget, forecast, and revenue-target rows for proposal review.',
    iconRef: 'assets/connectors/finance.svg',
    format: 'file',
    inputHint: 'CSV with Role/Person, Cost Rate, Rate Unit, Currency, Target, Revenue, Budget, or Forecast columns',
    folderKinds: ['orgchart', 'strategy', 'grc', 'workspace'],
    targets: ['folder', 'both'],
    compatibleProfileIds: ['orgchart', 'gantt', 'grc-control'],
    source: {
      system: 'finance',
      defaultIntent: 'generic_table',
      requiresIntentResolution: true,
      retentionClass: 'customer_configured',
      volume: 'high',
    },
    status: 'beta',
    expectedColumns: ['Role', 'Person', 'Cost Rate', 'Rate Unit', 'Currency', 'Target', 'Revenue'],
    adapter: () =>
      import('../services/adapters/CsvUploadAdapter').then(
        (m) => new m.CsvUploadAdapter() as never,
      ),
  });

  safeRegister({
    id: 'niche-system-export',
    label: 'Niche system export',
    description: 'Import operational exports from niche systems through the shared mapping resolver.',
    iconRef: 'assets/connectors/generic-source.svg',
    format: 'file',
    inputHint: 'CSV with a stable source key plus domain fields for the selected folder',
    folderKinds: [
      'orgchart',
      'process',
      'network',
      'grc',
      'strategy',
      'software_design',
      'workspace',
    ],
    targets: ['folder', 'both'],
    compatibleProfileIds: [
      'orgchart',
      'process',
      'network',
      'grc-control',
      'gantt',
      'component',
    ],
    source: {
      system: 'niche_system',
      defaultIntent: 'generic_table',
      requiresIntentResolution: true,
      retentionClass: 'customer_configured',
      volume: 'medium',
    },
    status: 'beta',
    expectedColumns: ['External ID', 'Name', 'Status', 'Owner'],
    adapter: () =>
      import('../services/adapters/CsvUploadAdapter').then(
        (m) => new m.CsvUploadAdapter() as never,
      ),
  });

  safeRegister({
    id: 'azure-ad',
    label: 'Azure Active Directory',
    description: 'Connect to Microsoft Entra ID directory.',
    iconRef: '/assets/app-icons/data-integration/entra.png',
    format: 'oauth',
    folderKinds: ['orgchart'],
    targets: ['diagram', 'folder', 'both'],
    compatibleProfileIds: ['orgchart'],
    source: {
      system: 'azure_ad',
      defaultIntent: 'people_directory',
      emitsDeltas: true,
      retentionClass: 'rolling_window',
      volume: 'high',
    },
    status: 'stable',
    adapter: () =>
      import('../services/adapters/AzureAdAdapter').then(
        (m) => new m.AzureAdAdapter() as never,
      ),
  });

  safeRegister({
    id: 'bamboohr',
    label: 'BambooHR',
    description: 'Connect to BambooHR employee directory.',
    iconRef: '/assets/app-icons/data-integration/bamboohr.png',
    format: 'api-key',
    folderKinds: ['orgchart'],
    targets: ['diagram', 'folder', 'both'],
    compatibleProfileIds: ['orgchart'],
    source: {
      system: 'bamboohr',
      defaultIntent: 'people_directory',
      supportedIntents: ['people_directory', 'person_hierarchy'],
      emitsDeltas: true,
      retentionClass: 'rolling_window',
      volume: 'high',
      authorityTypes: ['people_identity', 'position_structure'],
      maxSensitivity: 'manager',
      yjsSafe: false,
      enablementGates: ['contract', 'identity', 'recovery', 'security', 'scale', 'product'],
    },
    status: 'stable',
    adapter: () =>
      import('../services/adapters/BambooHrAdapter').then(
        (m) => new m.BambooHrAdapter() as never,
      ),
  });

  safeRegister({
    id: 'hibob',
    label: 'HiBob',
    description: 'Connect to the HiBob employee directory.',
    iconRef: '/assets/app-icons/data-integration/hibob.svg',
    format: 'api-key',
    folderKinds: ['orgchart'],
    targets: ['diagram', 'folder', 'both'],
    compatibleProfileIds: ['orgchart'],
    source: {
      system: 'hibob',
      defaultIntent: 'people_directory',
      retentionClass: 'rolling_window',
      volume: 'high',
    },
    status: 'coming-soon',
    salesInterestId: 'atlas.hibob',
    adapter: () => {
      throw new Error('[hibob] not yet implemented');
    },
  });

  safeRegister({
    id: 'workday',
    label: 'Workday',
    description: 'Connect to Workday HCM.',
    format: 'oauth',
    folderKinds: ['orgchart'],
    targets: ['diagram', 'folder', 'both'],
    compatibleProfileIds: ['orgchart'],
    source: {
      system: 'workday',
      defaultIntent: 'people_directory',
      supportedIntents: ['people_directory', 'person_hierarchy', 'position_hierarchy', 'org_structure'],
      emitsDeltas: true,
      retentionClass: 'rolling_window',
      volume: 'high',
      authorityTypes: ['people_identity', 'position_structure'],
      maxSensitivity: 'manager',
      yjsSafe: false,
      enablementGates: ['contract', 'identity', 'recovery', 'security', 'scale', 'product'],
    },
    status: 'coming-soon',
    salesInterestId: 'atlas.workday',
    adapter: () =>
      import('../services/adapters/WorkdayAdapter').then(
        (m) => new m.WorkdayAdapter() as never,
      ),
  });

  safeRegister({
    id: 'google-workspace',
    label: 'Google Workspace',
    description: 'Connect to Google Workspace directory.',
    iconRef: '/assets/app-icons/data-integration/google-workspace.png',
    format: 'oauth',
    folderKinds: ['orgchart'],
    targets: ['diagram', 'folder', 'both'],
    compatibleProfileIds: ['orgchart'],
    source: {
      system: 'google_workspace',
      defaultIntent: 'people_directory',
      emitsDeltas: true,
      retentionClass: 'rolling_window',
      volume: 'high',
    },
    status: 'stable',
    adapter: () =>
      import('../services/adapters/GoogleWorkspaceAdapter').then(
        (m) => new m.GoogleWorkspaceAdapter() as never,
      ),
  });

  safeRegister({
    id: 'sop-document',
    label: 'Policy document',
    description: 'Extract processes from an SOP or policy document (PDF or Word).',
    iconRef: 'assets/connectors/pdf.svg',
    format: 'file',
    inputHint: 'PDF or Word (.docx) policy document',
    folderKinds: ['process'],
    targets: ['folder'],
    compatibleProfileIds: ['process'],
    source: {
      system: 'sop_document',
      defaultIntent: 'process_inventory',
      retentionClass: 'audit_retained',
      volume: 'medium',
    },
    status: 'stable',
    adapter: () =>
      import('../services/adapters/SopDocumentAdapter').then(
        (m) => new m.SopDocumentAdapter() as never,
      ),
  });

  safeRegister({
    id: 'genogram-case-document',
    label: 'Case notes or transcript',
    description: 'Upload or paste PDF, Word, or text notes and generate a genogram draft.',
    iconRef: 'assets/connectors/pdf.svg',
    format: 'file',
    inputHint: 'PDF, Word (.docx), text (.txt), or pasted case notes',
    folderKinds: ['genogram'],
    targets: ['folder'],
    compatibleProfileIds: ['genogram'],
    source: {
      system: 'genogram_case_document',
      defaultIntent: 'genogram_case_source',
      retentionClass: 'audit_retained',
      volume: 'low',
    },
    status: 'stable',
    adapter: () =>
      import('../services/adapters/GenogramCaseDocumentAdapter').then(
        (m) => new m.GenogramCaseDocumentAdapter() as never,
      ),
  });

  safeRegister({
    id: 'grc-register',
    label: 'GRC Register',
    description: 'Import controls, risks, and findings from a GRC register CSV.',
    iconRef: 'assets/connectors/grc.svg',
    format: 'file',
    inputHint: 'CSV with NIST-style control or risk register columns — see the templates below',
    folderKinds: ['process', 'grc'],
    targets: ['folder'],
    compatibleProfileIds: ['grc-control'],
    source: {
      system: 'csv',
      defaultIntent: 'control_risk_register',
      retentionClass: 'audit_retained',
      volume: 'medium',
    },
    status: 'beta',
    expectedColumns: ['Control Identifier', 'Control Name', 'Control_Text', 'Related Controls'],
    templates: [
      { label: 'Controls register — NIST 800-53 (sample)', href: '/sample-data/grc-controls-nist-sample.csv' },
      { label: 'Risk register (sample)', href: '/sample-data/grc-risk-register-sample.csv' },
    ],
    adapter: () =>
      import('../services/adapters/GrcRegisterAdapter').then(
        (m) => new m.GrcRegisterAdapter() as never,
      ),
  });
}

type RegisterArgs = Parameters<typeof registerConnector>[0];

function safeRegister(manifest: RegisterArgs): void {
  try {
    registerConnector(manifest);
  } catch (err) {
    // Duplicate registration from a hot reload or double-bootstrap — ignore;
    // any other error is a real bug and should propagate.
    if (err instanceof Error && err.message.includes('duplicate connector id')) return;
    throw err;
  }
}
